<template>
	<section class="page page-redirect">
		<div class="redirect-content">
			<h2>Opening ChalkySticks...</h2>

			<p>
				If the app doesn't open automatically,
				<a v-bind:href="fallbackUrl" v-on:click="Handle_OnClickOpenWeb">click here</a>
				.
			</p>
		</div>
	</section>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import PageBase from '@/Core/Page.vue';
	import { Component, Prop } from 'vue-property-decorator';
	import { beforeDestroy, mounted } from '@/Utility/Decorators';

	/**
	 * Smart redirect page that attempts to open the native app first,
	 * then falls back to the web version with preserved routing
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Page
	 * @project ChalkySticks App
	 */
	@Component
	export default class PageRedirect extends PageBase {
		/**
		 * The deep link path extracted from the URL
		 */
		public deepLinkPath: string = '';

		/**
		 * The fallback web URL
		 */
		public fallbackUrl: string = '';

		/**
		 * Timeout reference for cleanup
		 */
		private redirectTimeout: number | null = null;

		/**
		 * Initialize the redirect logic on component mount
		 *
		 * @return void
		 */
		@mounted
		public setup(): void {
			this.extractDeepLinkPath();
			this.buildFallbackUrl();
			this.attemptAppLaunch();
		}

		/**
		 * Extracts the path after /redirect from the current URL
		 *
		 * @return void
		 */
		private extractDeepLinkPath(): void {
			const currentPath = this.$route.path;
			const redirectPrefix = '/redirect';

			if (currentPath.startsWith(redirectPrefix)) {
				this.deepLinkPath = currentPath.substring(redirectPrefix.length);

				// Remove leading slash if present
				if (this.deepLinkPath.startsWith('/')) {
					this.deepLinkPath = this.deepLinkPath.substring(1);
				}
			}
		}

		/**
		 * Builds the fallback web URL with preserved routing
		 *
		 * @return void
		 */
		private buildFallbackUrl(): void {
			const baseUrl = location.origin;

			this.fallbackUrl = this.deepLinkPath ? `${baseUrl}/${this.deepLinkPath}` : baseUrl;
		}

		/**
		 * Attempts to launch the native app using custom URL scheme
		 *
		 * @return void
		 */
		private attemptAppLaunch(): void {
			const appScheme = this.deepLinkPath ? `chalkysticks://${this.deepLinkPath}` : 'chalkysticks://';

			// Try to open the native app
			window.location.href = appScheme;

			// Set fallback timeout
			this.redirectTimeout = window.setTimeout(() => {
				this.Handle_OnClickOpenWeb();
			}, 2000);
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * Redirects to the web version of the app
		 *
		 * @return Promise<void>
		 */
		protected async Handle_OnClickOpenWeb(): Promise<void> {
			window.location.href = this.fallbackUrl;
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.page-redirect {
		align-items: center;
		display: flex;
		justify-content: center;
		text-align: center;
	}
</style>
